'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { solicitudService } from '@/lib/api/solicitudService';
import { useAuth } from '@/hooks/useAuth';
import { Solicitud } from '@/types/solicitud';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import ErrorMessage from '@/components/common/ErrorMessage';

const columnasBase = [
  'FECHA PROSPECTO',
  'AÑO CREDITO',
  'PROMOTOR',
  'EMPRESA CONVENIO',
  'FUENTE',
  'TIPO DE PRODUCTO',
  'ETAPA',
  'ESTATUS',
  'FECHA COTIZACIÓN',
  'FECHA PROBABLE DE CIERRE',
  'MES PROBABLE DE CIERRE',
  'FECHA CIERRE',
  'MES DE CIERRE',
  'FECHA PERDIDO',
  'MONTO SOLICITADO',
  'MONTO AUTORIZADO',
  'TIPO DE VENTA EXTERNA O INTERNA',
  'MOTIVO NO CIERRE',
  'TAMAÑO CUENTA',
  'TIPO DE NEGOCIO',
  'CN',
  'NUEVO/RENOVACION',
];


const toMonth = (date?: string) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatDate = (date?: string) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/** Primer día del mes actual en YYYY-MM-DD */
const getFirstDayOfCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

/** Último día del mes actual en YYYY-MM-DD */
const getLastDayOfCurrentMonth = () => {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
};

const inRange = (value?: string, start?: string, end?: string) => {
  if (!value) return false;
  const v = new Date(value);
  if (Number.isNaN(v.getTime())) return false;
  if (start) {
    const s = new Date(start);
    if (!Number.isNaN(s.getTime()) && v < s) return false;
  }
  if (end) {
    const e = new Date(end);
    if (!Number.isNaN(e.getTime()) && v > e) return false;
  }
  return true;
};

const cuentaTamano = (monto?: number) => {
  const val = monto || 0;
  if (val < 10000) return 'Chica';
  if (val < 50000) return 'Mediana';
  return 'Grande';
};

const ROIP_COLUMNS = ['FECHA PROSPECTO', 'AÑO CREDITO', 'PROMOTOR', 'EMPRESA CONVENIO', 'FUENTE', 'TIPO DE PRODUCTO', 'ETAPA', 'ESTATUS', 'FECHA COTIZACIÓN'] as const;

const ReporteVentasManagement: React.FC = () => {
  const { isFieldAllowed, isAuthenticated, isLoading: authLoading } = useAuth();
  const columnas = useMemo(() => {
    return columnasBase.filter((c) => {
      if (ROIP_COLUMNS.includes(c as typeof ROIP_COLUMNS[number])) {
        return isFieldAllowed(c);
      }
      return true;
    });
  }, [isFieldAllowed]);
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [criterio, setCriterio] = useState('PROMOTOR');
  const [valor, setValor] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  const load = async () => {
    setIsLoading(true);
    setError(null);
    const res = await solicitudService.getAllSummary();
    if (res.data) setSolicitudes(res.data);
    else setError(res.error || 'Error al cargar solicitudes');
    setIsLoading(false);
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      load();
    }
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    if (columnas.length > 0 && !columnas.includes(criterio)) {
      setCriterio(columnas[0]);
    }
  }, [columnas, criterio]);

  const rows = useMemo(() => {
    const rows = solicitudes.map((s) => {
      const fechaContacto = s.fechaContacto || '';
      const fechaPropuesta = s.fechaPropuesta || '';
      const fechaPerdido = s.fechaRechazo || '';
      return {
        '_fechaProspectoRaw': fechaContacto, // Campo interno para filtrado
        'FECHA PROSPECTO': formatDate(fechaContacto),
        'AÑO CREDITO': fechaContacto ? new Date(fechaContacto).getFullYear().toString() : '',
        'PROMOTOR': s.asesorNombre || '',
        'EMPRESA CONVENIO': s.clienteEmpresa || '',
        'FUENTE': s.clienteSeEnteroPor || '',
        'TIPO DE PRODUCTO': s.producto || '',
        'ETAPA': s.etapaEmbudo || '',
        'ESTATUS': s.estatusEmbudo || '',
        'FECHA COTIZACIÓN': formatDate(fechaContacto),
        'FECHA PROBABLE DE CIERRE': formatDate(fechaPropuesta),
        'MES PROBABLE DE CIERRE': toMonth(fechaPropuesta),
        'FECHA CIERRE': formatDate(fechaPropuesta),
        'MES DE CIERRE': toMonth(fechaPropuesta),
        'FECHA PERDIDO': formatDate(fechaPerdido),
        'MONTO SOLICITADO': s.montoSolicitado ?? 0,
        'MONTO AUTORIZADO': s.montoAutorizado ?? 0,
        'TIPO DE VENTA EXTERNA O INTERNA': s.clienteTipoEmpresa || '',
        'MOTIVO NO CIERRE': s.motivoNoCierre || '',
        'TAMAÑO CUENTA': cuentaTamano(s.montoSolicitado),
        'TIPO DE NEGOCIO': s.tipoDeNegocio || '',
        'CN': s.solicitudNumeroControl || '',
        'NUEVO/RENOVACION': s.clienteNuevoRenovacion || '',
      };
    });

    return rows.filter((r) => {
      // Solo aplicar filtro de fechas si hay fechas especificadas
      if (fechaInicio || fechaFin) {
        const fechaRaw = r['_fechaProspectoRaw'];
        // Si hay filtro de fecha pero el registro no tiene fecha, excluirlo
        if (!fechaRaw) return false;
        if (!inRange(fechaRaw, fechaInicio || undefined, fechaFin || undefined)) return false;
      }
      // Filtro por criterio/valor
      if (!valor.trim()) return true;
      const cell = String((r as Record<string, any>)[criterio] ?? '');
      return cell.toLowerCase().includes(valor.toLowerCase());
    });
  }, [solicitudes, criterio, valor, fechaInicio, fechaFin]);

  const downloadCsv = () => {
    const header = columnas.join(',');
    const lines = rows.map((r) =>
      columnas.map((c) => `"${String((r as Record<string, any>)[c] ?? '').replace(/"/g, '""')}"`).join(',')
    );
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'ReporteVentas.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reporte de Ventas</h1>
          <p className="text-sm text-gray-500 mt-1">Reporte de ventas.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load}>Recargar</Button>
          <Button onClick={downloadCsv}>Descargar CSV</Button>
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Criterio</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={columnas.includes(criterio) ? criterio : columnas[0] ?? ''}
              onChange={(e) => setCriterio(e.target.value)}
            >
              {columnas.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <Input label="Valor" value={valor} onChange={(e) => setValor(e.target.value)} />
          <Input label="Fecha inicio" type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
          <Input label="Fecha fin" type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="min-w-[1600px] w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              {columnas.map((c) => (
                <th key={c} className="text-left p-3">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r, idx) => (
              <tr key={idx} className="hover:bg-blue-50/40 transition-colors">
                {columnas.map((c) => (
                  <td key={`${idx}-${c}`} className="p-3">{String((r as Record<string, any>)[c] ?? '') || '—'}</td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td className="p-6 text-gray-500" colSpan={columnas.length}>Sin registros</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReporteVentasManagement;
